export default function UnderstandingTenantRights() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <i className="ri-shield-check-line"></i>
              <span>Legal Protection</span>
            </div>
            
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Understanding Tenant Rights During Property Sales
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 leading-relaxed">
              When selling an apartment building with tenants in place, understanding tenant rights is crucial to avoid legal complications and ensure a smooth transaction. Federal, state, and local laws provide significant protections to tenants, even when ownership changes hands.
            </p>

            <p className="text-slate-700 mb-6 leading-relaxed">
              The most fundamental principle is that <strong>existing leases survive the sale of the property</strong>. This means that when you sell your apartment building, the new owner must honor all existing lease agreements, including rent amounts, lease terms, and tenant rights. Tenants cannot be forced to move simply because the property has been sold.
            </p>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <i className="ri-alert-line text-amber-600 text-2xl mt-1"></i>
                <div>
                  <h4 className="font-semibold text-amber-900 mb-2">Important Legal Requirement</h4>
                  <p className="text-amber-800 text-sm leading-relaxed">
                    In most states, tenants must receive written notice of the property sale, including the new owner's contact information. Failure to provide proper notice can result in legal penalties and delayed closings.
                  </p>
                </div>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4">Key Tenant Rights You Must Respect:</h3>

            <div className="space-y-4">
              <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-text-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Lease Continuation Rights</h4>
                  <p className="text-slate-600 text-sm">
                    All existing lease terms remain valid through the end of the lease period. The new owner steps into your shoes as landlord and must honor every provision of the original lease agreement.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-home-4-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Right to Quiet Enjoyment</h4>
                  <p className="text-slate-600 text-sm">
                    Tenants have the right to peaceful occupancy without excessive disruption. While property showings are necessary, you must provide reasonable notice (typically 24-48 hours) and limit showing frequency.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Security Deposit Protection</h4>
                  <p className="text-slate-600 text-sm">
                    Security deposits must be properly transferred to the new owner with full accounting. Tenants retain all rights to their deposits, and the new owner becomes responsible for returning them at lease end.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-notification-3-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Notice Requirements</h4>
                  <p className="text-slate-600 text-sm">
                    Most states require 30-90 days written notice before any changes to tenancy terms. During the sale process, tenants must be notified of ownership transfer and provided with new landlord contact information.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-user-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Anti-Retaliation Protection</h4>
                  <p className="text-slate-600 text-sm">
                    You cannot retaliate against tenants who exercise their legal rights, such as requesting repairs or filing complaints. Any attempt to force tenants out before a sale can result in significant legal liability.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-hand-coin-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">First Right of Refusal (Some Jurisdictions)</h4>
                  <p className="text-slate-600 text-sm">
                    In certain cities and states, tenants may have the right of first refusal to purchase the property before it's sold to a third party. Check your local laws to ensure compliance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/5c992280c4b8adb82a7b586eaa1d0f61.jpg"
                alt="Understanding tenant rights and legal protections during apartment building sales"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border border-slate-100 max-w-sm">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <i className="ri-scales-3-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">100%</div>
                  <div className="text-sm text-slate-600">Lease Protection</div>
                </div>
              </div>
              <p className="text-xs text-slate-600">
                All existing leases remain fully enforceable after property sale
              </p>
            </div>
          </div>
        </div>

        <div className="mt-16 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">State-Specific Tenant Protections</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <i className="ri-map-pin-line text-teal-600"></i>
                California
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>90-day notice for no-fault evictions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Rent control in many cities</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Just cause eviction requirements</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <i className="ri-map-pin-line text-teal-600"></i>
                New York
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Strong rent stabilization laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Tenant right of first refusal</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Strict security deposit rules</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <i className="ri-map-pin-line text-teal-600"></i>
                Texas
              </h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>More landlord-friendly laws</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>30-day notice standard</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-teal-600 mt-0.5"></i>
                  <span>Faster eviction process</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-sm text-slate-600 mt-6 text-center">
            <i className="ri-information-line text-teal-600"></i> Always consult with a local real estate attorney to understand specific tenant rights in your jurisdiction
          </p>
        </div>
      </div>
    </section>
  );
}
